import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './PacketView.css';
import { loadTournamentsData, loadTournamentById } from '../services/publicTournamentService';
import Sidebar from '../components/Sidebar';

const PacketView = ({ packet, onReturn, onNavigate }) => {
  const { packetId, questionIndex } = useParams();
  const navigate = useNavigate();
  const questionRefs = useRef({});

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(true);
  const [expandedAnswers, setExpandedAnswers] = useState(new Set());
  const [highlightedQuestion, setHighlightedQuestion] = useState(null);
  
  // NEW: State for loaded data
  const [currentPacket, setCurrentPacket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // NEW: Load packet data from Supabase
  useEffect(() => {
    const loadPacket = async () => {
      // If packet is passed as prop, use it
      if (packet) {
        setCurrentPacket(packet);
        setLoading(false);
        return;
      }

      // If packetId in URL, load from Supabase
      if (packetId) {
        try {
          setLoading(true);
          console.log('Loading packet from Supabase, ID:', packetId);
          
          const numericPacketId = parseInt(packetId, 10);
          const packetData = await loadTournamentById(numericPacketId);
          
          console.log('✓ Packet loaded:', packetData.title);
          setCurrentPacket(packetData);
          setError(null);
        } catch (err) {
          console.error('Error loading packet:', err);
          setError(err.message);
        } finally {
          setLoading(false);
        }
      } else {
        // No packet prop and no packetId in URL
        setCurrentPacket({ title: "No packet selected", questions_data: [] });
        setLoading(false);
      }
    };

    loadPacket();
  }, [packetId, packet]);

  const questions = currentPacket?.questions_data || [];

  useEffect(() => {
  if (questions.length > 0) {
    console.log('=== IMAGE DIAGNOSTIC ===');
    console.log('First question:', questions[0]);
    console.log('First question image:', questions[0].image);
    
    // Check all questions for images
    const questionsWithImages = questions.filter(q => q.image);
    console.log(`Questions with images: ${questionsWithImages.length} out of ${questions.length}`);
    questionsWithImages.forEach((q, i) => {
      console.log(`Question ${i + 1} image:`, q.image);
    });
  }
}, [questions]);

  // Highlight specific question when :questionNumber is in the URL
  useEffect(() => {
    if (questionIndex && questions.length > 0) {
      const qNumber = parseInt(questionIndex, 10);

      if (qNumber >= 1 && qNumber <= questions.length) {
        setHighlightedQuestion(qNumber); // highlight only

        const element = questionRefs.current[qNumber];
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    } else {
      setHighlightedQuestion(null);
    }
  }, [questionIndex, questions]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
    document.body.style.overflow = sidebarOpen ? 'auto' : 'hidden';
  };

  const handleOverlayClick = () => {
    setSidebarOpen(false);
    document.body.style.overflow = 'auto';
  };

  useEffect(() => {
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  const toggleAnswer = (questionIndex) => {
    const newExpanded = new Set(expandedAnswers);
    if (newExpanded.has(questionIndex)) newExpanded.delete(questionIndex);
    else newExpanded.add(questionIndex);
    setExpandedAnswers(newExpanded);
  };

  const expandAll = () => {
    const allIndices = new Set(questions.map((_, index) => index));
    setExpandedAnswers(allIndices);
  };

  const collapseAll = () => {
    setExpandedAnswers(new Set());
  };

  const handleReturn = () => {
    if (onReturn) onReturn();
    else if (packetId) navigate('/');
  };

  const answersViewed = expandedAnswers.size;
  const progressPercentage = questions.length > 0 ? Math.round((answersViewed / questions.length) * 100) : 0;

  // Show loading state
  if (loading) {
    return (
      <div className={`app-container ${!darkMode ? 'light-mode' : ''}`}>
        <div className="main-content">
          <div className="packet-content">
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>იტვირთება პაკეტი...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className={`app-container ${!darkMode ? 'light-mode' : ''}`}>
        <div className="main-content">
          <div className="packet-content">
            <div className="error-state">
              <h3>⚠️ შეცდომა</h3>
              <p>პაკეტის ჩატვირთვა ვერ მოხერხდა: {error}</p>
              <button className="return-button" onClick={handleReturn}>
                უკან დაბრუნება
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show empty state
  if (!currentPacket) {
    return (
      <div className={`app-container ${!darkMode ? 'light-mode' : ''}`}>
        <div className="main-content">
          <div className="packet-content">
            <div className="empty-state">
              <h3>პაკეტი ვერ მოიძებნა</h3>
              <button className="return-button" onClick={handleReturn}>
                უკან დაბრუნება
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`app-container ${!darkMode ? 'light-mode' : ''}`}>
      {/* Mobile burger menu */}
      <button className="burger-menu" onClick={toggleSidebar}>
        <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none">
          <path d="M3 12h18M3 6h18M3 18h18"></path>
        </svg>
      </button>

      {sidebarOpen && (
        <div className="sidebar-overlay visible" onClick={handleOverlayClick}></div>
      )}

      <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <button className="sidebar-close" onClick={toggleSidebar} aria-label="Close sidebar">
          <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none">
            <path d="M18 6L6 18M6 6l12 12"></path>
          </svg>
        </button>
        <div className="sidebar-content-wrapper">
          <Sidebar darkMode={darkMode} setDarkMode={setDarkMode} />
        </div>
      </div>

      <div className={`main-content ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="packet-content">
          <div className="packet-header">
            <div className="packet-header-top">
              <div className="packet-meta">
                <div className="breadcrumb">
                  <span className="breadcrumb-item" onClick={handleReturn}>პაკეტი</span>
                  <span className="breadcrumb-separator">›</span>
                  <span className="breadcrumb-current">{currentPacket.title}</span>
                  {questionIndex && (
                    <>
                      <span className="breadcrumb-separator">›</span>
                      <span className="breadcrumb-current">კითხვა {questionIndex}</span>
                    </>
                  )}
                </div>
                <h1 className="packet-title">{currentPacket.title}</h1>
                <div className="packet-info">
                  <div className="info-item">
                    <span className="info-label">კითხვების რაოდენობა:</span>
                    <span className="info-value">{questions.length}</span>
                  </div>
                  {highlightedQuestion && (
                    <div className="info-item highlighted-info">
                      <span className="info-label">მონიშნული კითხვა:</span>
                      <span className="info-value">#{highlightedQuestion}</span>
                    </div>
                  )}
                </div>
              </div>
              <button className="return-button" onClick={handleReturn}>
                <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none">
                  <path d="M19 12H6m6-7l-7 7 7 7" />
                </svg>
                უკან დაბრუნება
              </button>
            </div>

            {questions.length > 0 && (
              <div className="bulk-actions">
                <button className="action-button secondary small" onClick={expandAll}>
                  <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none">
                    <path d="M3 8l4.5 4.5L12 8m0 8l4.5-4.5L12 16" />
                  </svg>
                  ყველას გახსნა
                </button>
                <button className="action-button secondary small" onClick={collapseAll}>
                  <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none">
                    <path d="M7.5 8L12 12.5L16.5 8m-9 8L12 11.5L16.5 16" />
                  </svg>
                  ყველას ჩაკეცვა
                </button>
                {highlightedQuestion && (
                  <button
                    className="action-button primary small"
                    onClick={() => {
                      setHighlightedQuestion(null);
                      if (packetId) navigate(`/packet/${packetId}`);
                    }}
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none">
                      <path d="M18 6L6 18M6 6l12 12"></path>
                    </svg>
                    მონიშვნის გაუქმება
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="questions-list">
            {questions.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">
                  <svg viewBox="0 0 24 24" width="48" height="48" stroke="currentColor" strokeWidth="1.5" fill="none">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.35-4.35"></path>
                  </svg>
                </div>
                <h3>No Questions Available</h3>
                <p>This packet doesn't contain any questions yet.</p>
                {packetId && <p className="debug-info">Packet ID: {packetId}</p>}
              </div>
            ) : (
              questions.map((question, index) => {
                const qNumber = index + 1;
                const isHighlighted = highlightedQuestion === qNumber;

                return (
                  <div
                    key={index}
                    className={`question-item ${isHighlighted ? 'highlighted' : ''}`}
                    ref={el => (questionRefs.current[String(qNumber)] = el)}
                    id={`question-${qNumber}`}
                  >
                    <div className={`question-card vertical ${isHighlighted ? 'highlighted-card' : ''}`}>
                      {isHighlighted && (
                        <div className="highlight-indicator">
                          <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none">
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                          </svg>
                          <span>მონიშნული კითხვა</span>
                        </div>
                      )}

                      <div className="question-header-vertical">
                        <h2 className={`question-number ${isHighlighted ? 'highlighted-number' : ''}`}>
                          კითხვა {qNumber}
                        </h2>
                      </div>

                      {question.image && question.image.src && question.image.id === 1 && (
                        <div className="question-image-container">
                          <img
                            src={question.image.src}
                            alt={`Question ${qNumber} image`}
                            className="question-image"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                      )}

                      <div className={`question-text ${isHighlighted ? 'highlighted-text' : ''}`}>
                        {question.text}
                      </div>

                      <button
                        className={`answer-toggle ${expandedAnswers.has(index) ? 'expanded' : ''} ${isHighlighted ? 'highlighted-toggle' : ''}`}
                        onClick={() => toggleAnswer(index)}
                      >
                        <span>
                          {expandedAnswers.has(index) ? 'პასუხის ჩაკეცვა' : 'პასუხის ჩვენება'}
                        </span>
                        <svg className="toggle-icon" viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none">
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </button>

                      <div className={`answer-dropdown ${expandedAnswers.has(index) ? 'expanded' : ''}`}>
                        <div className="answer-dropdown-content">
                          {question.answerImage && question.answerImage.src && (
                            <div className="answer-image-container">
                              <img
                                src={question.answerImage.src}
                                alt={`Answer for question ${qNumber}`}
                                className="answer-image"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            </div>
                          )}

                          <div className="answer-details">
                            <div className="answer-field">
                              <span className="field-label">პასუხი:</span>
                              <span className="field-value">{question.answer}</span>
                            </div>
                            
                            {question.comment && (
                              <div className="answer-field comment-field">
                                <span className="field-label">კომენტარი:</span>
                                <span className="field-value">{question.comment}</span>
                              </div>
                            )}

                            <div className="answer-field">
                              <span className="field-label">წყარო:</span>
                              <span className="field-value">
                                {question.source}
                                {question.image && question.image.src && question.image.id === 2 && (
                                  <img
                                    src={question.image.src}
                                    alt={`Source image for question ${qNumber}`}
                                    className="source-inline-image"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                )}
                              </span>
                            </div>

                            <div className="answer-field">
                              <span className="field-label">ავტორი:</span>
                              <span className="field-value">{question.author}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PacketView;